import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { 
  MapPin, Clock, Users, Calendar, DollarSign, Leaf, Check, 
  ChevronLeft, ChevronRight, Flame, Sparkles, Zap, ArrowRight,
  Share2, Heart, Star, Zap as Energy, Users as Staff, Recycle, Shield, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import BookingModal from '../components/BookingModal';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default eco/receipts content when fields are empty
const defaultEcoContent = {
  en: {
    energy: "Renewable energy sources",
    staff: "85%+ Local employment",
    waste: "Zero single-use plastic policy"
  },
  es: {
    energy: "Fuentes de energía renovable",
    staff: "85%+ Empleo local",
    waste: "Política cero plástico de un solo uso"
  }
};

const defaultReceiptsContent = {
  en: {
    intro: "Every adventure should leave a positive mark, not a footprint.",
    items: [
      "Local First: We prioritize hiring from local communities, ensuring tourism dollars stay where they belong.",
      "Small Groups Only: We keep group sizes small to minimize environmental impact and maximize authentic experiences.",
      "Carbon Conscious: All transportation is optimized to reduce emissions wherever possible.",
      "Community Partnerships: We work directly with local organizations to ensure tourism benefits residents.",
      "A portion of every booking supports local conservation and education initiatives."
    ],
    verifiedBy: ["Sustainable Travel International", "Exciting.Travel Verified"]
  },
  es: {
    intro: "Cada aventura debe dejar una marca positiva, no una huella.",
    items: [
      "Local Primero: Priorizamos contratar de comunidades locales, asegurando que el turismo beneficie donde pertenece.",
      "Solo Grupos Pequeños: Mantenemos grupos pequeños para minimizar el impacto ambiental y maximizar experiencias auténticas.",
      "Conscientes del Carbono: Todo el transporte está optimizado para reducir emisiones siempre que sea posible.",
      "Alianzas Comunitarias: Trabajamos directamente con organizaciones locales para asegurar que el turismo beneficie a los residentes.",
      "Una porción de cada reserva apoya iniciativas locales de conservación y educación."
    ],
    verifiedBy: ["Sustainable Travel International", "Exciting.Travel Verificado"]
  }
};

// Eco Badge Popup Component
const EcoBadgePopup = ({ trip, language }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  if (!trip.sustainable) return null;
  
  // Use custom content or fall back to defaults
  const ecoEnergy = (language === 'es' ? trip.eco_energy_es : trip.eco_energy) || defaultEcoContent[language]?.energy || defaultEcoContent.en.energy;
  const ecoStaff = (language === 'es' ? trip.eco_staff_es : trip.eco_staff) || defaultEcoContent[language]?.staff || defaultEcoContent.en.staff;
  const ecoWaste = (language === 'es' ? trip.eco_waste_es : trip.eco_waste) || defaultEcoContent[language]?.waste || defaultEcoContent.en.waste;
  
  return (
    <div 
      className="absolute top-4 right-4 z-20"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Badge */}
      <div className="px-3 py-2 rounded-full bg-[#84bc00] text-white flex items-center gap-1.5 shadow-lg cursor-pointer transition-transform hover:scale-105">
        <Leaf className="w-4 h-4" />
        <span className="text-sm font-bold">ECO</span>
      </div>
      
      {/* Popup - Always shows with defaults */}
      {isHovered && (
        <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 p-4 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="absolute -top-2 right-6 w-4 h-4 bg-white border-l border-t border-gray-100 rotate-45"></div>
          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Leaf className="w-4 h-4 text-[#84bc00]" />
            {language === 'es' ? 'Impacto Ecológico' : 'Eco Impact'}
          </h4>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                <Energy className="w-4 h-4 text-yellow-600" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase">{language === 'es' ? 'Energía' : 'Energy'}</p>
                <p className="text-sm text-gray-700">{ecoEnergy}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Staff className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase">{language === 'es' ? 'Personal' : 'Staff'}</p>
                <p className="text-sm text-gray-700">{ecoStaff}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <Recycle className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase">{language === 'es' ? 'Residuos' : 'Waste'}</p>
                <p className="text-sm text-gray-700">{ecoWaste}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TripDetail = () => {
  const { id } = useParams();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  // Check for cancelled booking
  useEffect(() => {
    if (searchParams.get('booking_cancelled') === 'true') {
      toast.info(language === 'es' ? 'Reserva cancelada' : 'Booking cancelled');
    }
  }, [searchParams, language]);

  const burnIcons = { 1: Sparkles, 2: Flame, 3: Zap };
  const burnColors = {
    1: 'from-purple-500 to-pink-500',
    2: 'from-orange-500 to-amber-500',
    3: 'from-red-500 to-orange-600'
  };
  const burnBgColors = {
    1: 'bg-purple-600',
    2: 'bg-orange-500',
    3: 'bg-red-600'
  };
  const burnDescriptions = {
    1: { en: 'Light activity, 2-4 hrs/day. Perfect for first-timers.', es: 'Actividad ligera, 2-4 hrs/día. Perfecto para principiantes.' },
    2: { en: 'Moderate activity, 4-6 hrs/day. The sweet spot.', es: 'Actividad moderada, 4-6 hrs/día. El punto perfecto.' },
    3: { en: 'Intense activity, 6-8+ hrs/day. For the committed.', es: 'Actividad intensa, 6-8+ hrs/día. Para los comprometidos.' }
  };

  useEffect(() => {
    const fetchTrip = async () => {
      try {
        const response = await fetch(`${API_URL}/api/packages/${id}?lang=${language}`);
        if (!response.ok) throw new Error('Trip not found');
        const data = await response.json();
        setTrip(data);
        if (data.dates && data.dates.length > 0) {
          setSelectedDate(data.dates[0]);
        }
      } catch (error) {
        console.error('Error fetching trip:', error);
        navigate('/trips');
      } finally {
        setLoading(false);
      }
    };
    fetchTrip();
  }, [id, language, navigate]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getAllImages = () => {
    if (!trip) return [];
    const images = [trip.image];
    if (trip.images && trip.images.length > 0) {
      images.push(...trip.images);
    }
    return images.filter(Boolean);
  };

  const nextImage = () => {
    const images = getAllImages();
    setActiveImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    const images = getAllImages();
    setActiveImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-96 bg-gray-200 rounded-2xl"></div>
            <div className="h-12 bg-gray-200 rounded w-1/3"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!trip) return null;

  const BurnIcon = burnIcons[trip.burn_level] || Flame;
  const burnGradient = burnColors[trip.burn_level] || burnColors[2];
  const burnBg = burnBgColors[trip.burn_level] || burnBgColors[2];
  const burnDesc = burnDescriptions[trip.burn_level] || burnDescriptions[2];
  const images = getAllImages();

  return (
    <div className="min-h-screen bg-gray-50 pt-20" data-testid="trip-detail-page">
      {/* Back Navigation */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Button
            onClick={() => navigate('/trips')}
            variant="ghost"
            className="text-gray-600 hover:text-gray-900"
            data-testid="back-to-trips-btn"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            {language === 'es' ? 'Todos los Viajes' : 'All Trips'}
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content - Left Side */}
          <div className="lg:col-span-2 space-y-8">
            {/* Image Gallery */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl" data-testid="trip-gallery">
              <div className="aspect-[16/10] relative">
                <img
                  src={images[activeImageIndex]}
                  alt={trip.name}
                  className="w-full h-full object-cover"
                />
                
                {/* Burn Level Badge */}
                <div className={`absolute top-4 left-4 px-4 py-2 rounded-full ${burnBg} text-white flex items-center gap-2 shadow-lg`}>
                  <BurnIcon className="w-5 h-5" />
                  <span className="font-bold">{trip.burn_label}</span>
                </div>

                {/* Sustainable Badge with Popup */}
                <EcoBadgePopup trip={trip} language={language} />

                {/* Image Navigation */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                      data-testid="prev-image-btn"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                      data-testid="next-image-btn"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                    
                    {/* Image Dots */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {images.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setActiveImageIndex(idx)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            idx === activeImageIndex ? 'bg-white w-6' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Trip Header */}
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <div className="flex items-center gap-1 text-gray-500 text-sm">
                  <MapPin className="w-4 h-4" />
                  <span>{trip.location}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-500 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>{trip.duration}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-500 text-sm">
                  <Users className="w-4 h-4" />
                  <span>{trip.groupSize}</span>
                </div>
              </div>
              
              <h1 className="text-3xl sm:text-4xl font-black text-gray-900 mb-4" data-testid="trip-name">
                {trip.name}
              </h1>

              {/* Vibes */}
              <div className="flex flex-wrap gap-2 mb-6">
                {trip.vibes?.map((vibe, i) => (
                  <span 
                    key={i}
                    className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-full capitalize"
                  >
                    {vibe === 'adventure' ? '🏔️' : vibe === 'culture' ? '🎭' : '🌴'} {vibe}
                  </span>
                ))}
              </div>

              {/* Description */}
              <p className="text-gray-600 text-lg leading-relaxed">
                {trip.description}
              </p>
            </div>

            {/* Burn Level Explanation */}
            <div className={`bg-gradient-to-r ${burnGradient} rounded-2xl p-6 text-white`} data-testid="burn-level-section">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <BurnIcon className="w-8 h-8" />
                </div>
                <div>
                  <div className="text-white/80 text-sm font-bold mb-1">
                    {language === 'es' ? 'NIVEL DE INTENSIDAD' : 'INTENSITY LEVEL'} {trip.burn_level}
                  </div>
                  <h3 className="text-2xl font-black mb-2">{trip.burn_label}</h3>
                  <p className="text-white/90">
                    {language === 'es' ? burnDesc.es : burnDesc.en}
                  </p>
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4" />
                    <span>{trip.effort_hours} {language === 'es' ? 'horas de actividad/día' : 'hours activity/day'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Highlights */}
            {trip.highlights && trip.highlights.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-lg" data-testid="highlights-section">
                <h2 className="text-2xl font-black text-gray-900 mb-6">
                  {language === 'es' ? 'Lo Más Destacado' : 'Trip Highlights'}
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {trip.highlights.map((highlight, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${burnGradient} flex items-center justify-center flex-shrink-0`}>
                        <Star className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-700 font-medium">{highlight}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* What's Included */}
            {trip.included && trip.included.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-lg" data-testid="included-section">
                <h2 className="text-2xl font-black text-gray-900 mb-6">
                  {language === 'es' ? '¿Qué Incluye?' : "What's Included"}
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {trip.included.map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#84bc00]/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-[#84bc00]" />
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* See The Receipts Section - Always shows with defaults */}
            {trip.sustainable && (
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 shadow-lg" data-testid="receipts-section">
                <h2 className="text-2xl font-black text-white mb-2 flex items-center gap-2">
                  <span>🧾</span>
                  {language === 'es' ? 'Mira Los Recibos: Por Qué Este Viaje Aprobó' : 'See The Receipts: Why This Trip Passed'}
                </h2>
                
                {/* Intro Text */}
                <p className="text-gray-300 mb-6 text-lg italic">
                  {(language === 'es' ? trip.receipts_intro_es : trip.receipts_intro) || 
                   (language === 'es' ? defaultReceiptsContent.es.intro : defaultReceiptsContent.en.intro)}
                </p>
                
                {/* Receipt Items */}
                <div className="space-y-4 mb-6">
                  {((trip.receipts_items && trip.receipts_items.length > 0) 
                    ? (language === 'es' && trip.receipts_items_es?.length > 0 ? trip.receipts_items_es : trip.receipts_items)
                    : (language === 'es' ? defaultReceiptsContent.es.items : defaultReceiptsContent.en.items)
                  ).map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#84bc00] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <p className="text-gray-200">{item}</p>
                    </div>
                  ))}
                </div>
                
                {/* Verified By Section */}
                <div className="border-t border-gray-700 pt-4">
                  <h3 className="text-sm font-bold text-[#84bc00] mb-3 flex items-center gap-2 uppercase tracking-wide">
                    <Shield className="w-4 h-4" />
                    {language === 'es' ? 'Verificado Por' : 'Verified By'}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {((trip.receipts_verified_by && trip.receipts_verified_by.length > 0)
                      ? (language === 'es' && trip.receipts_verified_by_es?.length > 0 ? trip.receipts_verified_by_es : trip.receipts_verified_by)
                      : (language === 'es' ? defaultReceiptsContent.es.verifiedBy : defaultReceiptsContent.en.verifiedBy)
                    ).map((verifier, i) => (
                      <span 
                        key={i} 
                        className="px-3 py-1.5 bg-gray-700/50 text-gray-300 rounded-full text-sm font-medium"
                      >
                        {verifier}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Perfect For */}
            {trip.perfect_for && trip.perfect_for.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-lg" data-testid="perfect-for-section">
                <h2 className="text-2xl font-black text-gray-900 mb-6">
                  {language === 'es' ? 'Perfecto Para' : 'Perfect For'}
                </h2>
                <div className="flex flex-wrap gap-3">
                  {trip.perfect_for.map((tag, i) => {
                    const tagLabels = {
                      first_timer: { en: 'First-Timers', es: 'Principiantes', emoji: '🌟' },
                      relaxation_seeker: { en: 'Relaxation Seekers', es: 'Buscadores de Relax', emoji: '🧘' },
                      couples: { en: 'Couples', es: 'Parejas', emoji: '💑' },
                      culture_lover: { en: 'Culture Lovers', es: 'Amantes de la Cultura', emoji: '🎭' },
                      photographer: { en: 'Photographers', es: 'Fotógrafos', emoji: '📸' },
                      solo_traveler: { en: 'Solo Travelers', es: 'Viajeros Solos', emoji: '🎒' },
                      weekend_warrior: { en: 'Weekend Warriors', es: 'Guerreros de Fin de Semana', emoji: '⚡' },
                      social_butterfly: { en: 'Social Butterflies', es: 'Mariposas Sociales', emoji: '🦋' },
                      nature_lover: { en: 'Nature Lovers', es: 'Amantes de la Naturaleza', emoji: '🌿' },
                      adrenaline_junkie: { en: 'Adrenaline Junkies', es: 'Adictos a la Adrenalina', emoji: '🔥' },
                      transformation_seeker: { en: 'Transformation Seekers', es: 'Buscadores de Transformación', emoji: '🦋' },
                      bucket_lister: { en: 'Bucket Listers', es: 'Lista de Deseos', emoji: '✅' },
                      conservation_focused: { en: 'Conservation Focused', es: 'Enfocados en Conservación', emoji: '🌍' }
                    };
                    const label = tagLabels[tag] || { en: tag.replace('_', ' '), es: tag.replace('_', ' '), emoji: '✨' };
                    
                    return (
                      <span 
                        key={i}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full font-medium flex items-center gap-2"
                      >
                        <span>{label.emoji}</span>
                        <span>{language === 'es' ? label.es : label.en}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Right Side */}
          <div className="space-y-6">
            {/* Booking Card */}
            <div className="bg-white rounded-2xl p-6 shadow-xl sticky top-24" data-testid="booking-card">
              {/* Price */}
              <div className="mb-6">
                <div className="text-gray-500 text-sm mb-1">
                  {language === 'es' ? 'Desde' : 'From'}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-black text-gray-900">
                    ${trip.price?.toLocaleString()}
                  </span>
                  <span className="text-gray-500">
                    / {language === 'es' ? 'persona' : 'person'}
                  </span>
                </div>
              </div>

              {/* Available Dates */}
              {trip.dates && trip.dates.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    {language === 'es' ? 'Fechas Disponibles' : 'Available Dates'}
                  </label>
                  <div className="space-y-2">
                    {trip.dates.map((date, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedDate(date)}
                        data-testid={`date-option-${i}`}
                        className={`w-full p-3 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${
                          selectedDate === date
                            ? `border-[#84bc00] bg-[#84bc00]/5`
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Calendar className={`w-5 h-5 ${selectedDate === date ? 'text-[#84bc00]' : 'text-gray-400'}`} />
                        <span className={`font-medium ${selectedDate === date ? 'text-[#84bc00]' : 'text-gray-700'}`}>
                          {formatDate(date)}
                        </span>
                        {selectedDate === date && (
                          <Check className="w-5 h-5 text-[#84bc00] ml-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Spots Available */}
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 p-3 bg-gray-50 rounded-lg">
                <Users className="w-4 h-4" />
                <span>
                  <strong className="text-gray-900">{trip.spots}</strong> {language === 'es' ? 'lugares disponibles' : 'spots available'}
                </span>
              </div>

              {/* CTA Buttons */}
              <Button
                className={`w-full bg-gradient-to-r ${burnGradient} hover:opacity-90 text-white font-bold py-4 rounded-lg transition-all text-lg mb-3`}
                data-testid="book-now-btn"
                onClick={() => setIsBookingModalOpen(true)}
              >
                {language === 'es' ? 'Reservar Ahora' : 'Book Now'}
              </Button>
              
              <Button
                variant="outline"
                className="w-full border-2 border-gray-200 hover:border-[#84bc00] hover:text-[#84bc00] font-bold py-4 rounded-lg transition-all"
                data-testid="inquiry-btn"
                onClick={() => navigate('/consultation')}
              >
                {language === 'es' ? 'Hacer una Consulta' : 'Make an Inquiry'}
              </Button>

              {/* Share & Save */}
              <div className="flex gap-3 mt-4">
                <button className="flex-1 flex items-center justify-center gap-2 py-2 text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg transition-colors">
                  <Share2 className="w-4 h-4" />
                  <span className="text-sm font-medium">{language === 'es' ? 'Compartir' : 'Share'}</span>
                </button>
                <button className="flex-1 flex items-center justify-center gap-2 py-2 text-gray-500 hover:text-red-500 border border-gray-200 rounded-lg transition-colors">
                  <Heart className="w-4 h-4" />
                  <span className="text-sm font-medium">{language === 'es' ? 'Guardar' : 'Save'}</span>
                </button>
              </div>
            </div>

            {/* Trip Quick Facts */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-black text-gray-900 mb-4">
                {language === 'es' ? 'Datos Rápidos' : 'Quick Facts'}
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold">
                      {language === 'es' ? 'Duración' : 'Duration'}
                    </p>
                    <p className="text-gray-900 font-medium">{trip.duration}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold">
                      {language === 'es' ? 'Tamaño del Grupo' : 'Group Size'}
                    </p>
                    <p className="text-gray-900 font-medium">{trip.groupSize}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full ${burnBg} flex items-center justify-center`}>
                    <BurnIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold">
                      {language === 'es' ? 'Nivel de Intensidad' : 'Intensity Level'}
                    </p>
                    <p className="text-gray-900 font-medium">{trip.burn_label}</p>
                  </div>
                </div>

                {trip.sustainable && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#84bc00]/10 flex items-center justify-center">
                      <Leaf className="w-5 h-5 text-[#84bc00]" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-bold">
                        {language === 'es' ? 'Sostenibilidad' : 'Sustainability'}
                      </p>
                      <p className="text-gray-900 font-medium">
                        {language === 'es' ? 'Viaje Eco-Certificado' : 'Eco-Certified Trip'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        trip={trip}
        selectedDate={selectedDate}
        language={language}
      />
    </div>
  );
};

export default TripDetail;
